"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTracking } from "react-tracking";
import { FormControl, InputLabel, Select as Select$1, Box } from "@mui/material";
import { ColorTextError, ColorTextMedium, ColorBlue500, ColorWhite, ColorGray700, SizeBorderWidthMedium, SizeBorderWidthThin, ColorGray900, SpaceSm, ColorCostcoBlue, SpaceXs, FontSize200, SpaceXxs } from "@nxt-costco-com/forge-design-tokens";
import { ChevronDown } from "@nxt-costco-com/forge-icons/icons/ChevronDown";
import { Text } from "../../DataDisplay/Text/index.js";
import { InputError } from "../InputError/index.js";
import { COMPONENT_NAME } from "./constants.js";
import { SelectSkeleton } from "./view.skeleton.js";
import { getDataTestId } from "../../Util/index.js";
const Select = ({
  autoComplete = "",
  uniqueId = "",
  hasError = false,
  errorText = "",
  errorIconAriaLabel,
  onBlur,
  onChange,
  label,
  helptext = "",
  options = [],
  value = "",
  isLoading,
  isRequired = false
}) => {
  const { trackEvent } = useTracking();
  if (!((options == null ? void 0 : options.length) !== 0)) {
    console.log("Mandatory options are not passed");
    return null;
  }
  const errorTextId = uniqueId ? `SelectErrorMessage_${uniqueId}` : `SelectErrorMessage`;
  const helptextId = uniqueId ? `SelectHelptext_${uniqueId}` : `SelectHelptext`;
  const ariaDescribedBy = [
    hasError && errorText ? errorTextId : "",
    helptext ? helptextId : ""
  ].join(" ");
  const showErrorText = Boolean(hasError && errorText);
  const borderWidth = hasError ? SizeBorderWidthMedium : SizeBorderWidthThin;
  const labelColor = hasError ? ColorTextError : ColorTextMedium;
  const switchableFocusColor = hasError ? ColorTextError : ColorBlue500;
  const helperTextsStyles = {
    padding: `${SpaceXxs} ${SpaceSm} ${SpaceXxs} ${SpaceSm}`
  };
  const helpTextStyle = { padding: `0 ${SpaceSm} ${SpaceXxs} ${SpaceSm}` };
  if (isLoading) return /* @__PURE__ */ jsx(SelectSkeleton, { uniqueId });
  const onChangeHandler = (event) => {
    trackEvent({
      component: {
        /* @ts-expect-error this is needed to get the text of the option */
        trackedData: [event.target[event.target.selectedIndex].text]
      }
    });
    onChange == null ? void 0 : onChange(event);
  };
  const handleBlur = (e) => {
    onBlur == null ? void 0 : onBlur(e);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      FormControl,
      {
        fullWidth: true,
        size: "small",
        sx: {
          "&:focus, &:focus-within": {
            "& .MuiInputLabel-root": {
              color: switchableFocusColor,
              maxWidth: `calc(133% - 56px)`
              // To address transform state of label
            }
          },
          "&:hover": {
            "& .MuiInputLabel-root": {
              color: labelColor
            }
          },
          height: "40px"
        },
        children: [
          /* @__PURE__ */ jsx(
            InputLabel,
            {
              htmlFor: `Select_${uniqueId}`,
              id: `SelectLabel_${uniqueId}`,
              sx: [
                {
                  color: labelColor,
                  backgroundColor: ColorWhite,
                  maxWidth: value ? `calc(133% - 56px)` : `calc(100% - 56px)`
                  // 56 px is combination of all the spacings
                }
              ],
              children: label
            }
          ),
          /* @__PURE__ */ jsx(
            Select$1,
            {
              required: isRequired,
              inputProps: {
                autoComplete,
                ...hasError || helptext ? {
                  "aria-describedby": ariaDescribedBy,
                  "aria-invalid": hasError
                } : {},
                id: `Select_${uniqueId}`,
                sx: {
                  fontSize: FontSize200,
                  padding: `${SpaceXs} ${SpaceSm}`,
                  wordWrap: "break-word"
                }
              },
              label,
              labelId: `SelectLabel_${uniqueId}`,
              sx: {
                "& .MuiNativeSelect-icon": {
                  color: ColorCostcoBlue,
                  top: "auto",
                  width: "24px",
                  height: "24px",
                  right: SpaceSm
                },
                "&:focus, &:focus-within": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: SizeBorderWidthMedium,
                    borderColor: switchableFocusColor
                  }
                },
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth,
                    borderColor: hasError ? ColorTextError : ColorGray900
                  }
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth,
                  borderColor: hasError ? ColorTextError : ColorGray700
                },
                height: "100%"
              },
              IconComponent: ChevronDown,
              native: true,
              onChange: onChangeHandler,
              onBlur: handleBlur,
              value,
              "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
              children: options.map((option) => /* @__PURE__ */ jsx(
                "option",
                {
                  "aria-hidden": option == null ? void 0 : option.ariaHidden,
                  disabled: option == null ? void 0 : option.disabled,
                  value: option.value,
                  children: option.text
                },
                option.value
              ))
            }
          )
        ]
      }
    ),
    showErrorText && /* @__PURE__ */ jsx(Box, { sx: helperTextsStyles, children: /* @__PURE__ */ jsx(
      InputError,
      {
        hasError,
        errorText,
        errorTextId,
        errorIconA11yAttributes: {
          "aria-label": errorIconAriaLabel,
          "aria-hidden": false,
          focusable: false
        }
      }
    ) }),
    helptext && /* @__PURE__ */ jsx(
      Text,
      {
        color: "medium",
        id: helptextId,
        variant: "t7",
        sx: showErrorText ? helpTextStyle : helperTextsStyles,
        children: helptext
      }
    ),
    options.map(
      (option) => option.additionalField !== null && option.additionalField !== void 0 && value === option.value && option.additionalField
    )
  ] });
};
Select.displayName = COMPONENT_NAME;
export {
  Select
};
